import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import '../../styles/CalendarStyles.css';
import RoomCalendar from './RoomCalendar';
import RoomTypeCalendar from './RoomTypeCalendar';
import arrowRight from './arrow-right.svg';
import arrowLeft from './arrow-left.svg';
import arrowLeftDisabled from './arrow-left-disabled.svg';
import Page from '../../components/Page';
import { fetchAccommodations, getDomain } from '../../utils/functions';
import Spinner from '../../components/Spinner';
import { Room } from '../../types/accommodation';
import houseIcon from './house.svg';
import Button from '../../components/Button';
import { fetchRoom, getDrupalLink } from '../../utils/functions';

interface Props {
  idToken: string;
}

function OwnedRoomsPage(props: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { t, i18n } = useTranslation('owned_rooms');

  const domain = getDomain();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_OWNED_ROOMS_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_OWNED_ROOMS_PAGE_PYHA ?? '';

  const [rooms, setRooms] = useState<Room[]>([]);
  const [activeRoom, setActiveRoom] = useState<Room | undefined>();

  // Set the start date as first of the month to avoid overflowing,
  // incase today is near end of month date
  const today = new Date();
  today.setDate(1);
  const [startDate, setStartDate] = useState<Date>(today);
  const [nextMonth, setNextMonth] = useState<Date>(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    return date;
  });

  const drupalBaseUrl: { [key: string]: string } = {};
  drupalBaseUrl['ruka'] = process.env.REACT_APP_DRUPAL_BASE_URL_RUKA ?? '';
  drupalBaseUrl['pyha'] = process.env.REACT_APP_DRUPAL_BASE_URL_PYHA ?? '';

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const calendarDateFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
  };

  const languageFormats: { [key: string]: string } = {
    fi: 'fi-FI',
    en: 'en-US',
  };
  const dateFormatLanguage = languageFormats[i18n.language];

  useEffect(() => {
    async function getAccommodations() {
      const rukaData = await fetchAccommodations({
        token: props.idToken,
        resort: 'Ruka',
      });

      const pyhaData = await fetchAccommodations({
        token: props.idToken,
        resort: 'Pyha',
      });

      let roomList: Room[] = [];
      if (rukaData && pyhaData && rukaData.ownershipData) {
        const apartmentData = [...rukaData.result, ...pyhaData.result];

        roomList = await Promise.all(
          rukaData.ownershipData.rooms.map(async (room) => {
            const { roomTypeId, roomId } = room;
            const apartment = apartmentData.find(
              (accommodation) =>
                parseInt(accommodation.roomTypeId) === parseInt(roomTypeId)
            );

            if (!apartment) {
              // Returning an 'empty' object (that gets filtered anyway) due to
              // typescript complaining that an array might have undefined items.
              return {
                roomTypeId: '',
                roomId: '',
                name: '',
                url: '',
                resort: '',
              };
            }
            const variation = apartment.variations.find(
              (variation) =>
                parseInt(variation.field_hotellinx_id) ===
                parseInt(room.roomTypeId)
            );

            const productUrl = `${
              drupalBaseUrl[apartment.resort.toLowerCase()]
            }/${i18n.language}/product/${apartment.field_product_id}?pets=${
              variation?.field_pets_allowed ? '1' : '0'
            }`;

            let name = '';

            const roomData = await fetchRoom(roomId, roomTypeId, props.idToken);

            if (roomData && roomData.RoomName) {
              name = `${apartment.field_title[i18n.language]} - ${
                roomData.RoomName
              }`;
            } else {
              name = `${apartment?.field_title[i18n.language]} - ${t(
                'Room name not found'
              )}`;
            }

            return {
              roomTypeId,
              roomId,
              name,
              url: productUrl,
              resort: apartment.resort,
            };
          })
        );

        roomList = roomList.filter((room) => room.roomId !== '');
      }
      if (roomList.length > 0) {
        setRooms(roomList);
        setActiveRoom(roomList[0]);
      } else {
        setErrorMessage(
          t('There was an error fetching the accommodation data.')
        );
      }
      setIsLoading(false);
    }

    if (props.idToken) {
      getAccommodations();
    }
  }, [props.idToken, i18n.language]);

  function onRoomSelect(event: React.ChangeEvent<HTMLSelectElement>) {
    if (rooms) {
      const newActiveRoom = rooms.find(
        (room) => room.roomId === event.target.value
      );
      setActiveRoom(newActiveRoom);
    }
  }

  let mainContent = <Spinner />;

  const roomSelector = (
    <div className='w-full ml-auto flex flex-col justify-center py-8 px-6 bg-gray-400'>
      <div className='flex items-center mb-4'>
        <img
          src={houseIcon}
          alt={t('Room select icon') ?? 'Room select icon'}
          className='h-6 w-auto mr-4'
        />{' '}
        <p>{t('Pick an apartment')}:</p>
      </div>

      <select
        id='roomselector'
        name='roomselector'
        onChange={onRoomSelect}
        disabled={isLoading ? true : false}
        className={`bg-white rounded h-12 px-4 relative ${
          isLoading ? 'disabled:opacity-75' : ''
        }`}
      >
        {rooms &&
          rooms.map((room) => (
            <option value={room.roomId} key={room.roomId}>
              {room.name}
            </option>
          ))}
      </select>
    </div>
  );

  function calendarTopElements(calendarType: string) {
    let exampleData = [
      { class: 'own-reservation', title: t('Own reservation') },
      { class: 'free', title: t('Free') },
      { class: 'blocked', title: t('Reserved') },
    ];
    let title = activeRoom ? `${activeRoom.name} ${t('apartment')}` : '';

    if (calendarType === 'roomType') {
      exampleData = [
        { class: 'free', title: t('Free') },
        { class: 'limited', title: t('Limited availability') },
        { class: 'minimum', title: t('Almost full') },
        { class: 'blocked', title: t('Reserved') },
      ];
      title = t('Entire apartment type') ?? 'Entire apartment type';
    }

    return (
      <>
        <hr className='border-black my-6 border-1 border-b-0 mx-auto' />
        <div className='lg:flex justify-between'>
          <h3 className='my-6 text-xl lg:w-6/12'>{title}</h3>
          <div className='flex justify-around lg:justify-between lg:w-6/12'>
            {exampleData.map((example, i) => (
              <div
                key={i}
                className={`grow-1 basis-0 flex flex-col items-center react-calendar__tile react-calendar__month-view__days__day react-calendar__month-view__days__day--${example.class}`}
              >
                <abbr title=''></abbr>
                <p
                  dangerouslySetInnerHTML={{ __html: example.title }}
                  className='text-center text-sm lg:text-base my-2'
                ></p>
              </div>
            ))}
          </div>
        </div>
        <div className='flex justify-around mx-auto my-4'>
          {/* The margin-right takes into account the gap between the two calendar months on desktop. */}
          <h4 className='text-center capitalize font-bold lg:mr-[30px] w-full'>
            {startDate.toLocaleDateString(dateFormatLanguage, {
              month: 'long',
            })}{' '}
            {startDate.getFullYear()}
          </h4>
          {!isTabletOrMobile && (
            <h4 className='text-center capitalize font-bold w-full'>
              {nextMonth.toLocaleDateString(dateFormatLanguage, {
                month: 'long',
              })}{' '}
              {nextMonth.getFullYear()}
            </h4>
          )}
        </div>
      </>
    );
  }

  function getNextMonth(baseDate: Date) {
    const date = new Date(baseDate);
    // Set date as first of the month to avoid
    // overflowing on months with days < 31.
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    return date;
  }

  function getPrevMonth(baseDate: Date) {
    const date = new Date(baseDate);
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return date;
  }

  function setToday() {
    setStartDate(today);
    setNextMonth(getNextMonth(today));
  }

  async function incrementMonth() {
    const newDate = getNextMonth(startDate);
    setStartDate(newDate);
    setNextMonth(getNextMonth(newDate));
  }

  async function decrementMonth() {
    const newDate = getPrevMonth(startDate);
    setStartDate(newDate);
    setNextMonth(getNextMonth(newDate));
  }

  // Prevent user from scrolling to past months.
  function disableDecrementMonth() {
    if (
      today.getFullYear() === startDate.getFullYear() &&
      today.getMonth() === startDate.getMonth()
    ) {
      return true;
    }
    return false;
  }

  const arrowClass = 'border-1 border-black rounded px-2 lg:px-6';
  const arrowDisabledClass =
    'border-1 !border-gray-300 !bg-gray-300 rounded px-2 lg:px-6';

  mainContent = (
    <div>
      {!isLoading && !errorMessage && activeRoom && (
        <Button
          external={true}
          href={getDrupalLink(activeRoom.url)}
          className='lg:w-fit lg:ml-auto mb-8'
        >
          {t('Book this apartment')}
        </Button>
      )}
      <div className='mx-auto border-1 border-gray-300 rounded-md bg-white w-full  py-4 px-2 lg:px-8'>
        <div className='2xl:w-[630px] mx-auto min-h-[1000px]'>
          {isLoading && (
            <div className='my-12'>
              <Spinner />
            </div>
          )}

          {errorMessage && <p className='text-red-500'>{errorMessage}</p>}

          {!isLoading && !errorMessage && (
            <>
              <div className='flex justify-between my-6'>
                <button
                  className={
                    disableDecrementMonth() ? arrowDisabledClass : arrowClass
                  }
                  disabled={disableDecrementMonth()}
                  onClick={decrementMonth}
                >
                  <img
                    src={
                      disableDecrementMonth() ? arrowLeftDisabled : arrowLeft
                    }
                    alt={t('Previous month') ?? 'Previous month'}
                  />
                </button>
                <button
                  onClick={setToday}
                  className='border-1 border-black rounded px-2 lg:px-6 text-sm lg:text-base'
                >
                  <h3>{t('This month')}</h3>
                </button>
                <button className={arrowClass} onClick={incrementMonth}>
                  <img src={arrowRight} alt={t('Next month') ?? 'Next month'} />
                </button>
              </div>
              {activeRoom && (
                <>
                  <RoomCalendar
                    activeRoom={activeRoom}
                    calendarTopElements={calendarTopElements}
                    isTabletOrMobile={isTabletOrMobile}
                    dateFormatLanguage={dateFormatLanguage}
                    calendarDateFormatOptions={calendarDateFormatOptions}
                    startDate={startDate}
                    idToken={props.idToken}
                  />
                  <RoomTypeCalendar
                    activeRoom={activeRoom}
                    calendarTopElements={calendarTopElements}
                    isTabletOrMobile={isTabletOrMobile}
                    dateFormatLanguage={dateFormatLanguage}
                    calendarDateFormatOptions={calendarDateFormatOptions}
                    startDate={startDate}
                    idToken={props.idToken}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Page
      pageId={pageId[domain] ?? ''}
      mainContent={mainContent}
      // Only show the room selector if we have more than one room.
      topContent={rooms && rooms?.length > 1 ? roomSelector : undefined}
    ></Page>
  );
}

export default OwnedRoomsPage;
